"use client";

import type { MouseEvent, ReactNode } from "react";

import { trackClickEvent, trackPhoneClick } from "@homeinstead/analytics";

type ExperimentalFeatureClickBoundaryProps = {
  children: ReactNode;
  variant: string;
};

const INTERACTIVE_SELECTOR = "a[href], button, [role='button']";

function getLinkTitle(element: HTMLElement): string | undefined {
  const ariaLabel = element.getAttribute("aria-label")?.trim();
  if (ariaLabel) {
    return ariaLabel;
  }

  const textContent = element.textContent?.trim();
  return textContent || undefined;
}

function getTargetUrl(element: HTMLElement): string | undefined {
  if (element instanceof HTMLAnchorElement) {
    return element.href || undefined;
  }

  return element.getAttribute("href") ?? undefined;
}

function ExperimentalFeatureClickBoundary({
  children,
  variant,
}: ExperimentalFeatureClickBoundaryProps) {
  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) {
      return;
    }

    const interactive = eventTarget.closest(INTERACTIVE_SELECTOR);
    if (!(interactive instanceof HTMLElement)) {
      return;
    }

    if (interactive instanceof HTMLButtonElement && interactive.disabled) {
      return;
    }

    const targetUrl = getTargetUrl(interactive);

    if (targetUrl?.startsWith("tel:")) {
      trackPhoneClick({ placement: "experimental feature" });
      return;
    }

    trackClickEvent("experimental_feature_link", {
      experimental_feature_variant: variant,
      link_title: getLinkTitle(interactive),
      page_zone: "sitebuilder",
      ...(targetUrl ? { target_url: targetUrl } : {}),
    });
  };

  return (
    <div className="contents" onClickCapture={handleClickCapture}>
      {children}
    </div>
  );
}

export { ExperimentalFeatureClickBoundary };
